'use client'

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import AdminPropertyDetails from '@/components/admin/AdminPropertyDetails'
import { PropertyWithLandlord } from '@/types'

export default function AdminPropertyDetailsPage() {
	const params = useParams()
	const navigate = useNavigate()
  const [property, setProperty] = useState<PropertyWithLandlord | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		const fetchData = async () => {
			try {
				const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
				const token = localStorage.getItem('authToken')
				const response = await fetch(`${apiUrl}/properties/${params.id}`, {
					headers: {
						'Authorization': token ? `Bearer ${token}` : '',
						'Content-Type': 'application/json'
					}
				})
				
				if (!response.ok) {
					throw new Error('Failed to load property')
				}
				
				const data = await response.json()
				const propertyData = data.property || data
				
				// Transform property data to match frontend format
				const transformedProperty = {
					...propertyData,
					id: propertyData._id || propertyData.id,
					landlord_id: propertyData.landlord?._id || propertyData.landlord || propertyData.landlord_id,
					// Extract and normalize image URLs if images is an array of objects
					images: (() => {
						const imageUrls = propertyData.images?.map((img: any) => typeof img === 'string' ? img : img.url) || propertyData.images || [];
						// Normalize localhost URLs to use backend URL from environment
						return imageUrls.map((url: string) => {
							if (!url) return url;
							if (url.includes('localhost:5001') || url.includes('127.0.0.1:5001')) {
								const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
								const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '');
								try {
									const urlObj = new URL(url);
									return `${apiBaseUrl}${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
								} catch {
									return url.replace(/https?:\/\/[^/]+/, apiBaseUrl);
								}
							} else if (url.startsWith('/uploads')) {
								const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
								const apiBaseUrl = apiUrl.replace(/\/api\/?$/, '');
								return `${apiBaseUrl}${url}`;
							}
							return url;
						});
					})(),
					// Extract document URLs if houseDocuments is an array of objects
					house_documents: propertyData.houseDocuments?.map((doc: any) => typeof doc === 'string' ? doc : doc.url) || propertyData.house_documents || [],
					// Extract video URLs if videos is an array of objects
					videos: propertyData.videos?.map((video: any) => typeof video === 'string' ? video : video.url) || propertyData.videos || [],
					// Build location from address
					location: propertyData.location || (propertyData.address && typeof propertyData.address === 'object' 
						? [propertyData.address.street, propertyData.address.city, propertyData.address.state].filter(Boolean).join(', ')
						: 'Location not specified'),
					// Map verification status
					is_verified: propertyData.isVerified !== undefined ? propertyData.isVerified : propertyData.is_verified,
					is_available: propertyData.isAvailable !== undefined ? propertyData.isAvailable : propertyData.is_available,
					// Map dates
					created_at: propertyData.createdAt || propertyData.created_at,
					// Transform landlord
					landlord: propertyData.landlord ? {
						...propertyData.landlord,
						id: propertyData.landlord._id || propertyData.landlord.id
					} : propertyData.landlord
				}
				
				setProperty(transformedProperty as any)
			} catch (e) {
				console.error('Error loading property:', e)
				setError('Failed to load property')
			} finally {
				setLoading(false)
			}
		}
		if (params.id) fetchData()
	}, [params.id])

  const handleVerify = async (propertyId: string, verified: boolean) => {
		try {
			const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
			const token = localStorage.getItem('authToken')
			const response = await fetch(`${apiUrl}/admin/properties/${propertyId}/verify`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': token ? `Bearer ${token}` : ''
				},
				body: JSON.stringify({ isVerified: verified })
			})
			
			if (response.ok) {
				await response.json() // Response data not used
				setProperty(prev => prev ? { ...prev, is_verified: verified } : prev)
				// Navigate back to dashboard to refresh the list
				navigate('/admin/dashboard?tab=properties')
			} else {
				const errorData = await response.json().catch(() => ({}))
				alert(errorData.message || 'Failed to verify property')
			}
		} catch (error) {
			console.error('Error verifying property:', error)
			alert('An error occurred while verifying the property')
		}
	}

  const handleDelete = async (propertyId: string) => {
		if (!confirm('Delete this property?')) return
		try {
			const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'
			const token = localStorage.getItem('authToken')
			const response = await fetch(`${apiUrl}/properties/${propertyId}`, {
				method: 'DELETE',
				headers: {
					'Authorization': token ? `Bearer ${token}` : ''
				}
			})
			
			if (response.ok) {
				navigate('/admin/dashboard?tab=properties')
			} else {
				const errorData = await response.json().catch(() => ({}))
				alert(errorData.message || 'Failed to delete property')
			}
		} catch (error) {
			console.error('Error deleting property:', error)
			alert('An error occurred while deleting the property')
		}
	}

	if (loading) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-gray-600">Loading...</div>
			</div>
		)
	}

	if (error || !property) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<p className="text-gray-700 mb-4">{error || 'Property not found'}</p>
					<button onClick={() => navigate('/admin/dashboard')} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Back to Dashboard</button>
				</div>
			</div>
		)
	}

	return (
		<div className="min-h-screen bg-gray-50">
			<header className="bg-white border-b">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<div className="flex items-center justify-between">
						<h1 className="text-xl font-semibold text-gray-900">Admin • Property Details</h1>
						<button onClick={() => navigate('/admin/dashboard')} className="text-blue-600 hover:text-blue-700">← Back</button>
					</div>
				</div>
			</header>
			<main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
				<AdminPropertyDetails property={property} onClose={() => navigate(-1)} onVerify={handleVerify} onDelete={handleDelete} />
			</main>
		</div>
	)
}


